import * as React from 'react';
import { useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { IconButton, UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { IconChevron } from '~shared/components/icons/standard';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { Pending } from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import {
  OnSelectQuizHandler,
  SelectQuiz,
} from '~teacher/launchv2/steps/SelectQuiz';
import { SelectSettings } from '~teacher/launchv2/steps/SelectSettings';
import { SelectWorkspace } from '~teacher/launchv2/steps/SelectWorkspace';
import { useBeginQuiz } from '~teacher/launchv2/useBeginQuiz';

import {
  isActivityStartingAtom,
  launchDialogStepAtom,
  quizNameAtom,
} from './atoms';
import {
  LaunchContextProvider,
  LaunchRoute,
} from './context/LaunchModalContext';
import { RoomProvider } from './context/RoomContext';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  initialRoomName: string;
  initialRoomId: number;
  isRoomRostered: boolean;
}
export function LaunchDialog({
  isOpen,
  initialRoomName,
  initialRoomId,
  isRoomRostered,
  onClose,
}: Props) {
  // what workspace is being used // or personal
  return isOpen ? (
    <LaunchContextProvider launchRoute={LaunchRoute.LaunchView}>
      <RoomProvider
        roomId={initialRoomId}
        roomName={initialRoomName}
        isRoomRostered={isRoomRostered}
      >
        <DialogComponent
          initialRoomName={initialRoomName}
          isOpen={isOpen}
          onClose={onClose}
        />
      </RoomProvider>
    </LaunchContextProvider>
  ) : null;
}

interface DialogTitleProps {
  currentStep: number;
  roomName: string;
  quizName: string;
  isActivityStarting: boolean;
}

function DialogTitle({
  currentStep,
  quizName,
  roomName,
  isActivityStarting,
}: DialogTitleProps) {
  const setStep = useSetAtom(launchDialogStepAtom);

  if (isActivityStarting) {
    return null;
  }

  return (
    <Container row verticalCenter style={{ height: '30px' }}>
      {quizName && currentStep !== 1 && currentStep !== 2 && (
        <div className={'mr-3'}>
          <IconButton
            aria-label={translate('Back')}
            onClick={() => setStep('decrement')}
          >
            <IconChevron size={'large'} direction={'left'} />
          </IconButton>
        </div>
      )}
      <span
        className={
          'font-display text-display leading-8 text-left truncate font-700 text-blue-900 hover:no-underline mr-8'
        }
      >
        {quizName
          ? quizName
          : translate('Launch Quiz in {0}').format(roomName.toUpperCase())}
      </span>
    </Container>
  );
}

interface DialogComponentProps {
  initialRoomName: string;

  isOpen: boolean;
  onClose: () => void;
}

function DialogComponent({
  initialRoomName,
  isOpen,
  onClose,
}: DialogComponentProps) {
  const isActivityStarting = useAtomValue(isActivityStartingAtom);
  const quizName = useAtomValue(quizNameAtom);
  const currentStep = useAtomValue(launchDialogStepAtom);

  return (
    <Dialog
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={onClose}
      id={'launch-quiz-dialog'}
      title={
        <DialogTitle
          roomName={initialRoomName}
          isActivityStarting={isActivityStarting}
          quizName={quizName}
          currentStep={currentStep}
        />
      }
      size="large"
    >
      {/* dialog doesnt unmount, so move to separate component to unmount properly */}
      {isOpen && (
        <>
          <div className="pt-2">
            <Divider />
          </div>
          <LaunchDialogContent />
        </>
      )}
    </Dialog>
  );
}

interface LaunchDialogContentProps {
  onSelectQuiz?: OnSelectQuizHandler;
}

export function LaunchDialogContent({
  onSelectQuiz,
}: LaunchDialogContentProps) {
  const currentStep = useAtomValue(launchDialogStepAtom);
  const isActivityStarting = useAtomValue(isActivityStartingAtom);
  const { beginQuiz } = useBeginQuiz();

  const appContext = useTeacherApplicationContext();

  // In embedded Assessments context, always use full height to ensure launch button sticks
  // In standalone Socrative context, use normal responsive height
  const heightClass =
    appContext === TeacherApplicationContext.ShowbieAssessments
      ? 'h-full'
      : 'h-full sm:h-modal-xl';

  // loading state
  if (isActivityStarting) {
    return (
      <div className="grid place-items-center h-full">
        <div className="flex flex-col items-center justify-center gap-8">
          <Pending />
          <span className="text-md leading-8 text-blue-900 font-display font-400">
            {translate('Starting Activity…')}
          </span>
        </div>
      </div>
    );
  }
  return (
    <>
      {/*NOTE: Changing this height will affect Showbie Integration view as well*/}
      <Container
        className={`flex-grow ${heightClass}`}
        style={
          {
            /* Override of style calculated by Container component */
          }
        }
      >
        {currentStep === 1 ? <SelectWorkspace /> : null}
        {currentStep === 2 ? <SelectQuiz onSelectQuiz={onSelectQuiz} /> : null}
        {currentStep === 3 && (
          <>
            <SelectSettings />
            <Divider />
            <Container className={'items-end pt-4'}>
              <UIButton
                appearance={'primary'}
                isDisabled={isActivityStarting}
                onClick={beginQuiz}
              >
                {translate('Launch')}
              </UIButton>
            </Container>
          </>
        )}
      </Container>
    </>
  );
}

export function useLaunchDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  return { isOpen, open, close };
}
